import { Injectable } from "@angular/core";
import { ShapeGeneratorService } from "../services/shape-generator/shape-generator.service";
import { getOriginalFaceMetaDataMap } from "src/utils/shape-facetype";
import { Subject } from "rxjs";
import { initialOrientation, Object3DUserData } from "~/src/utils/shape";
import { FaceIdentifierService } from "./face-identifier/face-identifier.service";
import { ShapeValidationService } from "../services/shape-validation.service";
import { Face, FaceType } from "../../../../utils/count-calculator/count-calculator.model";
import { MeshType } from "../../utils/shape-type";
import { Color, Mesh, MeshBasicMaterial, Vector3 } from "three";
import { OriginalColors } from "../../../../utils/shape";
import { StabilityService } from "../services/stability/stability.service";
import { SceneManagerService } from "../services/scene-manager/scene-manager.service";
import { ComponentInteractionService } from "../services/component-interaction/component-interaction.service";

@Injectable({
    providedIn: "root",
})
export class GenerateNewShapes {
    private isNewShapeGeneratedSubject = new Subject<boolean>();
    public isNewShapeGenerated$ = this.isNewShapeGeneratedSubject.asObservable();
    private isCurvedFace = false;
    private mesh: Mesh = new Mesh();

    constructor(
        private shapeGeneratorService: ShapeGeneratorService,
        private faceIdentifierService: FaceIdentifierService,
        private shapeValidationService: ShapeValidationService,
    ) {}

    private markAsGenerated(): void {
        this.isNewShapeGeneratedSubject.next(true);
    }

    public generateNewShapes(position: Vector3, shape: MeshType, color: Color): Mesh {
        // Generate shape based on the provided type
        if (Object.values(MeshType).includes(shape)) {
            this.mesh = this.shapeGeneratorService.generateShape(shape, color, false);
        }

        this.mesh.position.copy(position);

        // User data for the shape
        const faceMetaDataMap = getOriginalFaceMetaDataMap(this.mesh.name as MeshType);
        if (Object.keys(faceMetaDataMap!).length > 0) {
            this.mesh.userData[Object3DUserData.faceMetaDataMap] = faceMetaDataMap;
        }

        const originalColors: OriginalColors = [];

        (this.mesh.material as MeshBasicMaterial[]).map((material) => {
            if (material.name) {
                originalColors.push("#".concat(material.color.clone().getHexString().toUpperCase()));
            }
        });

        this.mesh.userData[Object3DUserData.originalColors] = originalColors;

        // Mark the object's orientation data
        this.mesh.userData[Object3DUserData.orientationData] = initialOrientation;

        let meshRealFace;
        if (this.faceIdentifierService.state.frontFace) {
            meshRealFace = this.getOrientationFace(0);
            this.isCurvedFace = this.checkCurvedFaceAndHalfPlane(Face.Back, meshRealFace, this.mesh)!;
        } else if (this.faceIdentifierService.state.rightFace) {
            meshRealFace = this.getOrientationFace(1);
            this.isCurvedFace = this.checkCurvedFaceAndHalfPlane(Face.Left, meshRealFace, this.mesh)!;
        } else if (this.faceIdentifierService.state.backFace) {
            meshRealFace = this.getOrientationFace(2);
            this.isCurvedFace = this.checkCurvedFaceAndHalfPlane(Face.Front, meshRealFace, this.mesh)!;
        } else if (this.faceIdentifierService.state.leftFace) {
            meshRealFace = this.getOrientationFace(3);
            this.isCurvedFace = this.checkCurvedFaceAndHalfPlane(Face.Right, meshRealFace, this.mesh)!;
        } else if (this.faceIdentifierService.state.topFace) {
            meshRealFace = this.getOrientationFace(4);
            this.isCurvedFace = this.checkCurvedFaceAndHalfPlane(Face.Bottom, meshRealFace, this.mesh)!;
        } else if (this.faceIdentifierService.state.bottomFace) {
            meshRealFace = this.getOrientationFace(5);
            this.isCurvedFace = this.checkCurvedFaceAndHalfPlane(Face.Top, meshRealFace, this.mesh)!;
        }

        if (this.isCurvedFace) {
            this.checkValidation(this.mesh);
        }

        // Notify that the shape has been generated
        this.markAsGenerated();

        return this.mesh;
    }

    private checkCurvedFaceAndHalfPlane(oppositeFace: Face, meshRealFace: Face | null, mesh: Mesh): boolean | null {
        const faceMetaDataMap = getOriginalFaceMetaDataMap(mesh.name as MeshType);
        if (!faceMetaDataMap) return null;
        const objectHit = this.faceIdentifierService.objectHit;

        const generateShapeName = mesh.name;
        const hitMeshName = objectHit.name;

        if (generateShapeName === hitMeshName && faceMetaDataMap[meshRealFace!].originalType === FaceType.HalfPlane) {
            this.mesh.rotation.set(objectHit.rotation.x, objectHit.rotation.y, objectHit.rotation.z);
            this.mesh.userData[Object3DUserData.orientationData] = objectHit.userData[Object3DUserData.orientationData];
            return false;
        }

        if (
            faceMetaDataMap[oppositeFace].originalType === FaceType.Curved ||
            faceMetaDataMap[oppositeFace].originalType === FaceType.HalfPlane
        ) {
            return true;
        }

        return false;
    }

    private getOrientationFace(index: number): Face | null {
        return this.faceIdentifierService.objectHit?.userData?.[Object3DUserData.orientationData]?.[index] ?? null;
    }

    public checkValidation(mesh: Mesh): void {
        this.shapeValidationService.validateAndFlipSequentially(mesh);
    }
}
